import { supabase } from "@/lib/supabase";

// =============================================================================
// Shared asset-photo upload. Used by both AssetPhotoGallery (post-create,
// manage photos) and PhotoCaptureField (during-create, quick reference shot).
// Produces one row in public.asset_photos + one object in the
// asset-documents bucket. T-051.
// =============================================================================

export type AssetKind = "vehicle" | "power_tool" | "hand_tool" | "rigging";

const BUCKET = "asset-documents";
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_MIME   = ["image/jpeg", "image/png", "image/webp", "image/heic"];

function storagePath(kind: AssetKind, id: string, file: File): string {
  const ext   = (file.name.split(".").pop() || "jpg").toLowerCase();
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const rand  = Math.random().toString(36).slice(2, 8);
  return `${kind}/${id}/${stamp}-${rand}.${ext}`;
}

/**
 * Upload a photo for an asset. Validates MIME + size, writes the object to
 * the private asset-documents bucket, then inserts the asset_photos row.
 * If the row insert fails, the storage object is rolled back.
 */
export async function uploadAssetPhoto(
  kind: AssetKind,
  assetId: string,
  file: File,
): Promise<void> {
  if (!ACCEPTED_MIME.includes(file.type)) {
    throw new Error("Unsupported image type. JPEG / PNG / WEBP / HEIC only.");
  }
  if (file.size > MAX_PHOTO_BYTES) {
    throw new Error(`Photo too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 10 MB).`);
  }

  const path = storagePath(kind, assetId, file);

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type,
    cacheControl: "3600",
    upsert: false,
  });
  if (upErr) throw upErr;

  const { error: rowErr } = await supabase.from("asset_photos").insert({
    asset_kind:   kind,
    asset_id:     assetId,
    storage_path: path,
    filename:     file.name,
    mime_type:    file.type,
    size_bytes:   file.size,
  });
  if (rowErr) {
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {});
    throw rowErr;
  }
}
